import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, ShieldCheck, ArrowRight, Clock, BookOpen, PlayCircle, Sparkles, LogOut, Lock } from "lucide-react";
import { authHeader, getUserToken, clearUserToken } from "@/utils/userAuth";
import { trackPurchase } from "@/utils/pixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const COUNTDOWN_KEY_PREFIX = "np_bridge_deadline_";
const COUNTDOWN_HOURS = 24;
const FULL_PRICE_EUR = 297;
const SALE_PRICE_EUR = 197;

const VALUE_ITEMS = [
  "Accesso immediato a tutte le 9 lezioni del protocollo (e ai futuri aggiornamenti)",
  "Schede pratiche stampabili — esercizi e frasi-modello per i 60 giorni",
  "Garanzia 60 giorni soddisfatto o rimborsato — scrivi a info@beetooagency.com",
  "Pagamento sicuro Stripe con la carta già verificata · One-Click",
];

function pad(n) { return String(n).padStart(2, "0"); }

export default function BridgeFullCoursePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseError, setPurchaseError] = useState("");
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!getUserToken()) { navigate("/?login=1"); return; }
    Promise.all([
      axios.get(`${API}/users/me`, { headers: authHeader() }),
      axios.get(`${API}/me/course`, { headers: authHeader() }),
    ])
      .then(([me, c]) => { setUser(me.data); setCourse(c.data); })
      .catch(() => { clearUserToken(); navigate("/?login=1"); })
      .finally(() => setLoading(false));
  }, [navigate]);

  // Persistent 24h countdown deadline per user
  const deadlineMs = useMemo(() => {
    if (!user) return null;
    const key = COUNTDOWN_KEY_PREFIX + user.id;
    let saved = parseInt(localStorage.getItem(key) || "0", 10);
    if (!saved || saved < Date.now()) {
      saved = Date.now() + COUNTDOWN_HOURS * 3600 * 1000;
      localStorage.setItem(key, String(saved));
    }
    return saved;
  }, [user]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = deadlineMs ? Math.max(0, deadlineMs - now) : 0;
  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  const expired = remaining <= 0;

  const handleBuy = async () => {
    setPurchaseLoading(true);
    setPurchaseError("");
    try {
      const { data } = await axios.post(`${API}/upsell/full_course`, {}, { headers: authHeader() });
      if (data.success) {
        // Tracking
        axios.post(`${API}/track`, { event_name: "purchase_full_course_success", payload: { amount_eur: SALE_PRICE_EUR, source: "bridge" } }, { headers: authHeader() }).catch(() => {});
        // Meta Pixel Purchase (deduped server-side via deterministic event_id)
        try { trackPurchase(`purchase_${user.id}_full_course`, SALE_PRICE_EUR, "EUR", "full_course"); } catch { /* ignore */ }
        navigate("/corso/area-membri?just_unlocked=1");
      } else {
        setPurchaseError(data.detail || "Pagamento non completato.");
      }
    } catch (e) {
      setPurchaseError(e?.response?.data?.detail || "Errore. Riprova fra poco.");
    } finally {
      setPurchaseLoading(false);
    }
  };

  if (loading) return <FullPageLoading />;
  if (!user) return null;
  // Access guard: only users who have lesson 3 unlocked can land here
  if (!user.has_access_l3) return <LockedPanel />;

  return (
    <main className="min-h-screen" style={{ background: "var(--np-tertiary)" }}>
      <header className="border-b" style={{ background: "var(--np-quaternary)", borderColor: "rgba(26,42,58,0.06)" }}>
        <div className="mx-auto px-6 md:px-10 py-2.5 flex items-center justify-between" style={{ maxWidth: 1280 }}>
          <img
            src="https://customer-assets.emergentagent.com/job_ciclo-responsabilita/artifacts/ypgcjh4z_11%20%2829%29.png"
            alt="Neuroscienza Pratica"
            className="block"
            style={{
              width: "clamp(220px, 24vw, 380px)",
              aspectRatio: "4 / 1",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
          <button onClick={() => { clearUserToken(); navigate("/"); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border" style={{ color: "var(--np-primary)", borderColor: "#dbe3ec" }}>
            <LogOut size={13} /> Esci
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto px-6 pt-16 md:pt-24 pb-10 text-center" style={{ maxWidth: 760 }}>
        <p className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#dbe3ec] text-[12px] font-semibold tracking-[0.2em] uppercase" style={{ color: "var(--np-primary)" }}>
          <Sparkles size={13} style={{ color: "var(--np-secondary)" }} /> Offerta riservata · solo 24 ore
        </p>
        <h1 className="mt-6 text-3xl md:text-5xl leading-[1.12] tracking-tight" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
          Hai visto le 3 lezioni.
          <span className="block italic mt-2" style={{ color: "var(--np-secondary)" }}>Ora puoi ottenere il corso completo.</span>
        </h1>
        <p className="mt-6 text-lg leading-[1.75]" style={{ color: "#2a3a4a" }}>
          Tutto il metodo Neuroscienza Pratica — Figlio a Carico, applicabile dalla prima settimana, con garanzia totale 60 giorni e accesso immediato alla piattaforma corso.
        </p>
      </section>

      {/* Countdown */}
      <section className="mx-auto px-6 mb-12" style={{ maxWidth: 760 }}>
        <div data-testid="bridge-countdown" className="bg-white rounded-2xl border border-[#e3eaf2] p-6 md:p-7 flex flex-col sm:flex-row items-center justify-between gap-5 shadow-[0_15px_50px_-30px_rgba(26,42,58,0.35)]">
          <div className="flex items-center gap-3">
            <Clock size={22} style={{ color: "var(--np-secondary)" }} />
            <p className="text-sm leading-snug" style={{ color: "var(--np-primary)" }}>
              {expired ? <strong>Offerta scaduta — il prezzo torna a {FULL_PRICE_EUR} €</strong>
                : <>Prezzo bloccato a <strong style={{ color: "var(--np-secondary)" }}>{SALE_PRICE_EUR} €</strong> per ancora:</>}
            </p>
          </div>
          <div className="flex gap-2">
            {[["Ore", pad(h)], ["Min", pad(m)], ["Sec", pad(s)]].map(([lbl, v]) => (
              <div key={lbl} className="w-14 sm:w-16 text-center rounded-xl px-2 py-2.5" style={{ background: expired ? "#f3f4f6" : "var(--np-primary)" }}>
                <p className="text-xl sm:text-2xl font-bold tabular-nums" style={{ color: expired ? "#9ca3af" : "#ffffff", fontFamily: "Merriweather, serif" }}>{v}</p>
                <p className="text-[10px] tracking-widest uppercase" style={{ color: expired ? "#9ca3af" : "rgba(255,255,255,0.7)" }}>{lbl}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lezioni che ti aspettano (dynamic from /api/me/course) */}
      <section className="mx-auto px-6 pb-6" style={{ maxWidth: 980 }}>
        <h2 className="text-2xl md:text-3xl text-center mb-3" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
          Le 6 lezioni che ti aspettano
        </h2>
        <p className="text-center text-sm md:text-base mb-8 max-w-2xl mx-auto" style={{ color: "var(--np-muted)" }}>
          Hai visto le prime 3 lezioni. Queste sono le 6 che completano il protocollo — le sblocchi tutte adesso.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5" data-testid="bridge-lessons-remaining">
          {(course?.lessons || []).filter(L => L.n >= 4).map((L) => (
            <div key={L.id} className="bg-white rounded-2xl border border-[#e3eaf2] p-5 md:p-6 flex gap-4 items-start">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-bold text-sm" style={{ background: "var(--np-quaternary)", color: "var(--np-secondary)", fontFamily: "Merriweather, serif" }}>
                {L.n}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold leading-snug" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
                  {L.title.replace(/^Lezione\s*\d+\s*[—-]\s*/, "")}
                </p>
                <p className="text-[12px] mt-1.5 inline-flex items-center gap-1.5" style={{ color: "var(--np-muted)" }}>
                  <PlayCircle size={13} style={{ color: "var(--np-secondary)" }} /> Video on-demand · accesso a vita
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Value */}
      <section className="mx-auto px-6 py-10" style={{ maxWidth: 760 }}>
        <div className="bg-white rounded-3xl border border-[#e3eaf2] p-7 md:p-10">
          <p className="text-[12px] tracking-[0.22em] uppercase font-semibold mb-4" style={{ color: "var(--np-secondary)" }}>Cosa ottieni con l'acquisto</p>
          <ul className="space-y-3">
            {VALUE_ITEMS.map((v) => (
              <li key={v} className="flex items-start gap-3 text-[16px] leading-relaxed" style={{ color: "#2a3a4a" }}>
                <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "var(--np-secondary)" }} />
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto px-6 pb-24" style={{ maxWidth: 760 }}>
        <div data-testid="bridge-cta-block" className="bg-white rounded-3xl text-center p-8 md:p-10" style={{ border: "4px solid transparent", backgroundImage: "linear-gradient(#ffffff,#ffffff), var(--np-tension)", backgroundOrigin: "border-box", backgroundClip: "padding-box, border-box" }}>
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="text-2xl line-through" style={{ color: "var(--np-muted)" }}>{FULL_PRICE_EUR} €</span>
            <span className="text-4xl md:text-5xl" style={{ color: "var(--np-secondary)", fontFamily: "Merriweather, serif" }}>{SALE_PRICE_EUR} €</span>
          </div>
          <p className="text-sm mb-7" style={{ color: "var(--np-muted)" }}>IVA inclusa · Pagamento One-Click con la stessa carta</p>

          {purchaseError && (
            <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 inline-block">{purchaseError}</p>
          )}

          <button data-testid="bridge-buy-btn" type="button" onClick={handleBuy} disabled={purchaseLoading} className="cta-btn disabled:opacity-70">
            {purchaseLoading ? (<><Loader2 className="animate-spin" size={18} /> Sto elaborando…</>) : (<>Sblocca il corso completo a {SALE_PRICE_EUR} € <ArrowRight size={18} strokeWidth={2.4} /></>)}
          </button>

          <p className="mt-5 text-[12px] flex items-center justify-center gap-1.5" style={{ color: "var(--np-muted)" }}>
            <ShieldCheck size={13} style={{ color: "var(--np-secondary)" }} /> Garanzia 60 giorni soddisfatto o rimborsato
          </p>
        </div>
      </section>
    </main>
  );
}

function FullPageLoading() {
  return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--np-tertiary)" }}><Loader2 className="animate-spin" size={26} style={{ color: "var(--np-secondary)" }} /></div>;
}

function LockedPanel() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--np-tertiary)" }}>
      <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: "var(--np-quaternary)" }}>
          <Lock size={24} style={{ color: "var(--np-secondary)" }} />
        </div>
        <h1 className="text-2xl" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>Completa prima la Lezione 3</h1>
        <a href="/corso/lezione/3" className="cta-btn mt-7 inline-flex">Vai alla Lezione 3</a>
      </div>
    </div>
  );
}
